import axios from 'axios';
import { API_URL } from '../constants/api';
import {
  WaiverTemplate,
  SignedWaiver,
  CreateWaiverTemplateRequest,
  UpdateWaiverTemplateRequest,
  SignWaiverRequest,
  SignWaiverAsGuestRequest,
  SignWaiverForChildRequest,
  WaiverStatusResponse,
  CanEditTemplateResponse,
  PlayerSearchResult
} from '../types/waiver';

// Field API client with field token
const fieldApiClient = axios.create({
  baseURL: API_URL,
});

fieldApiClient.interceptors.request.use((config) => {
  const fieldToken = localStorage.getItem('fieldToken');
  if (fieldToken) {
    config.headers.Authorization = `Bearer ${fieldToken}`;
  }
  return config;
});

// User API client with user token
const userApiClient = axios.create({
  baseURL: API_URL,
});

userApiClient.interceptors.request.use((config) => {
  const userToken = localStorage.getItem('token');
  if (userToken) {
    config.headers.Authorization = `Bearer ${userToken}`;
  }
  return config;
});

// Public API client (no auth needed)
const publicApiClient = axios.create({
  baseURL: API_URL,
});

// ==================== Public Waiver API ====================

/**
 * Get current active waiver template for a field
 */
export const getCurrentTemplate = async (fieldId: string): Promise<WaiverTemplate> => {
  const response = await publicApiClient.get(`/api/waiver/template/${fieldId}/current`);
  return response.data;
};

/**
 * Get waiver template for a specific year
 */
export const getTemplateByYear = async (fieldId: string, year: number): Promise<WaiverTemplate> => {
  const response = await publicApiClient.get(`/api/waiver/template/${fieldId}/year/${year}`);
  return response.data;
};

/**
 * Sign waiver as registered user
 */
export const signWaiver = async (request: SignWaiverRequest): Promise<{ waiverId: string; signedAt: string }> => {
  const response = await userApiClient.post('/api/waiver/sign', request);
  return response.data;
};

/**
 * Sign waiver as guest (no account)
 */
export const signWaiverAsGuest = async (request: SignWaiverAsGuestRequest): Promise<{ waiverId: string; signedAt: string }> => {
  const response = await publicApiClient.post('/api/waiver/sign/guest', request);
  return response.data;
};

/**
 * Sign waiver for child (parent signs)
 */
export const signWaiverForChild = async (request: SignWaiverForChildRequest): Promise<{ waiverId: string; signedAt: string }> => {
  const response = await userApiClient.post('/api/waiver/sign/child', request);
  return response.data;
};

/**
 * Check waiver status for current user
 */
export const checkWaiverStatus = async (fieldId: string): Promise<WaiverStatusResponse> => {
  const response = await userApiClient.get(`/api/waiver/check/${fieldId}`);
  return response.data;
};

/**
 * Get all signed waivers for current user
 */
export const getMyWaivers = async (): Promise<SignedWaiver[]> => {
  const response = await userApiClient.get('/api/waiver/my-waivers');
  return response.data;
};

/**
 * Get signed waiver by ID
 */
export const getSignedWaiver = async (id: string): Promise<SignedWaiver> => {
  const response = await userApiClient.get(`/api/waiver/${id}`);
  return response.data;
};

// ==================== Field Waiver Management API ====================

/**
 * Create new waiver template
 */
export const createTemplate = async (request: CreateWaiverTemplateRequest): Promise<{ templateId: string; version: number }> => {
  const formData = new FormData();
  formData.append('name', request.name);
  formData.append('year', request.year.toString());
  formData.append('sections', JSON.stringify(request.sections));
  formData.append('requiredFields', JSON.stringify(request.requiredFields));
  formData.append('signatureSettings', JSON.stringify(request.signatureSettings));
  formData.append('parentalConsentAgeThreshold', request.parentalConsentAgeThreshold.toString());

  const response = await fieldApiClient.post('/api/field/waiver/template', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Update waiver template
 */
export const updateTemplate = async (id: string, request: UpdateWaiverTemplateRequest): Promise<{ templateId: string }> => {
  const formData = new FormData();
  formData.append('name', request.name);
  formData.append('sections', JSON.stringify(request.sections));
  formData.append('requiredFields', JSON.stringify(request.requiredFields));
  formData.append('signatureSettings', JSON.stringify(request.signatureSettings));
  formData.append('parentalConsentAgeThreshold', request.parentalConsentAgeThreshold.toString());

  const response = await fieldApiClient.put(`/api/field/waiver/template/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Delete waiver template
 */
export const deleteTemplate = async (id: string): Promise<void> => {
  await fieldApiClient.delete(`/api/field/waiver/template/${id}`);
};

/**
 * Get all templates for field
 */
export const getAllTemplates = async (): Promise<WaiverTemplate[]> => {
  const response = await fieldApiClient.get('/api/field/waiver/templates');
  return response.data;
};

/**
 * Get template by ID
 */
export const getTemplate = async (id: string): Promise<WaiverTemplate> => {
  const response = await fieldApiClient.get(`/api/field/waiver/template/${id}`);
  return response.data;
};

/**
 * Copy template to another year
 */
export const copyTemplate = async (id: string, targetYear: number): Promise<{ templateId: string; year: number; version: number }> => {
  const response = await fieldApiClient.post(`/api/field/waiver/template/${id}/copy`, { targetYear });
  return response.data;
};

/**
 * Check if template can be edited
 */
export const canEditTemplate = async (id: string): Promise<CanEditTemplateResponse> => {
  const response = await fieldApiClient.get(`/api/field/waiver/template/${id}/can-edit`);
  return response.data;
};

/**
 * Activate template (make it the main one for the year)
 */
export const activateTemplate = async (id: string): Promise<void> => {
  await fieldApiClient.post(`/api/field/waiver/template/${id}/activate`);
};

export interface SignedWaiversResponse {
  items: SignedWaiver[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Get all signed waivers for field with pagination and search
 */
export const getSignedWaivers = async (
  year?: number,
  search?: string,
  page: number = 1,
  pageSize: number = 20
): Promise<SignedWaiversResponse> => {
  const params: Record<string, string | number> = { page, pageSize };
  if (year) params.year = year;
  if (search) params.search = search;
  const response = await fieldApiClient.get('/api/field/waiver/signed', { params });
  return response.data;
};

/**
 * Search signed waivers by name
 */
export const searchSignedWaivers = async (searchTerm: string): Promise<SignedWaiver[]> => {
  const response = await fieldApiClient.get('/api/field/waiver/signed/search', { params: { searchTerm } });
  return response.data;
};

/**
 * Get signed waiver by ID (field access)
 */
export const getFieldSignedWaiver = async (id: string): Promise<SignedWaiver> => {
  const response = await fieldApiClient.get(`/api/field/waiver/signed/${id}`);
  return response.data;
};

/**
 * Create new waiver template with optional image
 */
export const createTemplateWithImage = async (
  request: CreateWaiverTemplateRequest,
  imageFile?: File
): Promise<{ templateId: string; version: number }> => {
  const formData = new FormData();
  formData.append('name', request.name);
  formData.append('year', request.year.toString());
  formData.append('sections', JSON.stringify(request.sections));
  formData.append('requiredFields', JSON.stringify(request.requiredFields));
  formData.append('signatureSettings', JSON.stringify(request.signatureSettings));
  formData.append('parentalConsentAgeThreshold', request.parentalConsentAgeThreshold.toString());

  if (imageFile) {
    formData.append('imageFile', imageFile);
  }

  const response = await fieldApiClient.post('/api/field/waiver/template', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Update waiver template with optional image
 */
export const updateTemplateWithImage = async (
  id: string,
  request: UpdateWaiverTemplateRequest,
  imageFile?: File
): Promise<{ templateId: string }> => {
  const formData = new FormData();
  formData.append('name', request.name);
  formData.append('sections', JSON.stringify(request.sections));
  formData.append('requiredFields', JSON.stringify(request.requiredFields));
  formData.append('signatureSettings', JSON.stringify(request.signatureSettings));
  formData.append('parentalConsentAgeThreshold', request.parentalConsentAgeThreshold.toString());

  if (imageFile) {
    formData.append('imageFile', imageFile);
  }

  const response = await fieldApiClient.put(`/api/field/waiver/template/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};
